/**
 * Typed RFC 9457 error handling (ADR-0008, transport per ADR-0020). Every backend error is an
 * `application/problem+json` body with a stable `errorCode`; the fetch mutator throws an
 * ApiError carrying that parsed body — no ad-hoc body sniffing at call sites.
 */
import type { ProblemDetail } from "./generated/model";

export type ApiProblem = ProblemDetail;

/** Thrown by the mutator on any non-2xx response (fetch itself resolves on HTTP errors). */
export class ApiError extends Error {
  readonly status: number;
  readonly problem?: ProblemDetail;

  constructor(status: number, problem?: ProblemDetail) {
    super(problem?.detail ?? `Request failed with status ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.problem = problem;
  }
}

/** Narrow an unknown rejection to a failed request carrying a typed ProblemDetail body. */
export function isApiProblem(err: unknown): err is ApiError & { problem: ApiProblem } {
  return err instanceof ApiError && typeof err.problem?.errorCode === "string";
}

/** The ProblemDetail body of a failed request, or undefined when it isn't one. */
export function apiProblemOf(err: unknown): ApiProblem | undefined {
  return isApiProblem(err) ? err.problem : undefined;
}

/**
 * The canonical stale-write predicate (DOMAIN.md §B.5/§D.3, ADR-0055). A CAS save that lost the race
 * comes back as a 409 whose ProblemDetail carries `errorCode === "STALE_WRITE"`. This is THE check
 * every editable surface uses to trigger its conflict modal — never last-write-wins.
 */
export function isStaleWrite(err: unknown): err is ApiError & { problem: ApiProblem } {
  return isApiProblem(err) && (err.problem as { errorCode?: string }).errorCode === "STALE_WRITE";
}

/**
 * The authoritative CURRENT record embedded in a stale-write 409 (the conflict-modal contract): the
 * losing editor re-applies on this exact server state without a second fetch. Typed by the caller,
 * `undefined` when the error is not a stale write or the body omitted it (some seams carry only the
 * version). Terminal states (a CANCELLED booking) are never reopened — this drives "just cancelled by X".
 */
export function staleWriteCurrent<T>(err: unknown): T | undefined {
  if (!isStaleWrite(err)) return undefined;
  return (err.problem as { current?: T }).current;
}

/** The authoritative current version from a stale-write 409, or `undefined`. */
export function staleWriteVersion(err: unknown): number | undefined {
  if (!isStaleWrite(err)) return undefined;
  const v = (err.problem as { currentVersion?: unknown }).currentVersion;
  return typeof v === "number" ? v : undefined;
}

/**
 * The feature-locked predicate. A gated action the venue's plan does not include comes back as a 403
 * whose ProblemDetail carries `errorCode === "ENTITLEMENT_REQUIRED"` (with `flagKey` + `unlockHint`), or —
 * for the floor plan specifically — the bespoke `"FLOOR_PLAN_ENTITLEMENT_REQUIRED"`. Lets any surface show
 * "this feature isn't in your plan → upgrade" instead of a generic failure when the UI did not (or could
 * not) hide the action. Server-side enforcement is authoritative; this only drives the message.
 */
export function isEntitlementRequired(err: unknown): err is ApiError & { problem: ApiProblem } {
  if (!isApiProblem(err)) return false;
  const code = (err.problem as { errorCode?: string }).errorCode;
  return code === "ENTITLEMENT_REQUIRED" || code === "FLOOR_PLAN_ENTITLEMENT_REQUIRED";
}

/**
 * The EntitlementKey a feature-locked 403 refers to (e.g. "WAITLIST", "MULTI_SITE"), or `undefined`. The
 * bespoke floor-plan code carries no key, so it maps to "FLOOR_PLAN".
 */
export function entitlementFlagOf(err: unknown): string | undefined {
  if (!isEntitlementRequired(err)) return undefined;
  const problem = err.problem as { errorCode?: string; flagKey?: unknown };
  if (problem.errorCode === "FLOOR_PLAN_ENTITLEMENT_REQUIRED") return "FLOOR_PLAN";
  return typeof problem.flagKey === "string" ? problem.flagKey : undefined;
}

/** The per-call human "how to unlock" hint from a generic ENTITLEMENT_REQUIRED 403, or `undefined`. */
export function unlockHintOf(err: unknown): string | undefined {
  if (!isEntitlementRequired(err)) return undefined;
  const hint = (err.problem as { unlockHint?: unknown }).unlockHint;
  return typeof hint === "string" ? hint : undefined;
}

/**
 * Per-field validation messages from a 422 ProblemDetail (ADR-0008 `fieldErrors`), keyed by
 * field name — ready for react-hook-form's `setError` (ADR-0027).
 */
export function fieldErrorsOf(err: unknown): Record<string, string> {
  const problem = apiProblemOf(err) as
    | (ApiProblem & { fieldErrors?: Array<{ field?: string; message?: string }> })
    | undefined;
  const out: Record<string, string> = {};
  for (const fe of problem?.fieldErrors ?? []) {
    if (fe.field && fe.message && !(fe.field in out)) out[fe.field] = fe.message;
  }
  return out;
}
